import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, Trash2, RefreshCw } from "lucide-react";
import { PageHeader, inr } from "@/components/platform/platformUi";

const emptyForm = {
    name: "",
    slug: "",
    description: "",
    price_monthly: "",
    price_yearly: "",
    trial_days: "14",
    max_staff: "10",
    max_outlets: "1",
    features: "",
    highlighted: false,
    active: true,
    sort_order: "0",
};

export default function PlatformPlans() {
    const [plans, setPlans] = useState([]);
    const [form, setForm] = useState(emptyForm);
    const [editingId, setEditingId] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = () => {
        api.get("/platform/plans")
            .then((r) => setPlans(r.data || []))
            .catch(() => toast.error("Could not load plans"));
    };

    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setEditingId(null);
        setForm(emptyForm);
        setShowForm(true);
    };

    const openEdit = (p) => {
        setEditingId(p.id);
        setForm({
            name: p.name,
            slug: p.slug,
            description: p.description || "",
            price_monthly: String(p.price_monthly),
            price_yearly: String(p.price_yearly),
            trial_days: String(p.trial_days),
            max_staff: String(p.max_staff),
            max_outlets: String(p.max_outlets),
            features: (p.features || []).join("\n"),
            highlighted: !!p.highlighted,
            active: p.active !== false,
            sort_order: String(p.sort_order ?? 0),
        });
        setShowForm(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        const payload = {
            name: form.name.trim(),
            slug: form.slug.trim().toLowerCase(),
            description: form.description.trim(),
            price_monthly: Number(form.price_monthly),
            price_yearly: Number(form.price_yearly),
            trial_days: Number(form.trial_days),
            max_staff: Number(form.max_staff),
            max_outlets: Number(form.max_outlets),
            features: form.features.split("\n").map((s) => s.trim()).filter(Boolean),
            highlighted: form.highlighted,
            active: form.active,
            sort_order: Number(form.sort_order),
        };
        try {
            if (editingId) {
                await api.patch(`/platform/plans/${editingId}`, payload);
                toast.success("Plan updated");
            } else {
                await api.post("/platform/plans", payload);
                toast.success("Plan created");
            }
            setShowForm(false);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`Remove plan "${p.name}"?`)) return;
        setBusy(true);
        try {
            await api.delete(`/platform/plans/${p.id}`);
            toast.success("Plan removed");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed");
        } finally {
            setBusy(false);
        }
    };

    const syncRazorpay = async () => {
        setBusy(true);
        try {
            const r = await api.post("/platform/plans/sync-razorpay");
            if (r.data?.errors?.length) {
                toast.error(r.data.errors[0]?.error || "Some plans failed to sync — restart backend after updating .env");
            } else {
                toast.success(`Synced ${r.data?.synced?.length || 0} plan(s) to Razorpay`);
            }
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Razorpay sync failed — restart backend after updating .env");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-6 p-6 lg:p-8" data-testid="platform-plans-page">
            <PageHeader
                eyebrow="Subscription plans"
                title="SaaS plans"
                subtitle="Plans sync to Razorpay automatically — use Sync if you just updated API keys"
                action={
                    <div className="flex flex-wrap gap-2">
                        <Button type="button" variant="outline" disabled={busy} onClick={syncRazorpay}>
                            <RefreshCw className="mr-1.5 size-4" />
                            Sync Razorpay
                        </Button>
                        <Button
                            type="button"
                            onClick={openCreate}
                            className="bg-[#4A0E17] text-white hover:bg-[#3a0b12]"
                        >
                            <Plus className="mr-1.5 size-4" />
                            New plan
                        </Button>
                    </div>
                }
            />

            {showForm && (
                <form
                    onSubmit={submit}
                    className="space-y-4 rounded-xl border border-[#E8E4DE] bg-white p-5 shadow-[0_4px_22px_rgba(45,41,38,0.05)]"
                >
                    <p className="text-sm font-bold text-[#4A0E17]">{editingId ? "Edit plan" : "Create plan"}</p>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        <div className="space-y-1">
                            <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#3D2817]">Name</Label>
                            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#3D2817]">Slug</Label>
                            <Input value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} required />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#3D2817]">Sort order</Label>
                            <Input type="number" value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))} />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#3D2817]">Monthly (₹)</Label>
                            <Input type="number" min={0} value={form.price_monthly} onChange={(e) => setForm((f) => ({ ...f, price_monthly: e.target.value }))} required />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#3D2817]">Yearly (₹)</Label>
                            <Input type="number" min={0} value={form.price_yearly} onChange={(e) => setForm((f) => ({ ...f, price_yearly: e.target.value }))} required />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#3D2817]">Trial days</Label>
                            <Input type="number" min={0} value={form.trial_days} onChange={(e) => setForm((f) => ({ ...f, trial_days: e.target.value }))} />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#3D2817]">Max staff</Label>
                            <Input type="number" min={1} value={form.max_staff} onChange={(e) => setForm((f) => ({ ...f, max_staff: e.target.value }))} />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#3D2817]">Max outlets</Label>
                            <Input type="number" min={1} value={form.max_outlets} onChange={(e) => setForm((f) => ({ ...f, max_outlets: e.target.value }))} />
                        </div>
                    </div>
                    <div className="space-y-1">
                        <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#3D2817]">Description</Label>
                        <Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#3D2817]">Features (one per line)</Label>
                        <Textarea rows={4} value={form.features} onChange={(e) => setForm((f) => ({ ...f, features: e.target.value }))} />
                    </div>
                    <div className="flex flex-wrap gap-4">
                        <label className="flex items-center gap-2 text-sm font-medium text-[#3D2817]">
                            <input type="checkbox" checked={form.highlighted} onChange={(e) => setForm((f) => ({ ...f, highlighted: e.target.checked }))} />
                            Highlighted (popular — only one plan at a time)
                        </label>
                        <label className="flex items-center gap-2 text-sm font-medium text-[#3D2817]">
                            <input type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} />
                            Active
                        </label>
                    </div>
                    <div className="flex gap-2">
                        <Button type="submit" disabled={busy} className="bg-[#4A0E17] text-white hover:bg-[#3a0b12]">
                            {editingId ? "Save changes" : "Create plan"}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                    </div>
                </form>
            )}

            <div className="grid gap-4 lg:grid-cols-3">
                {plans.map((p) => (
                    <div
                        key={p.id}
                        className={`rounded-xl border bg-white p-5 shadow-[0_4px_22px_rgba(45,41,38,0.05)] ${
                            p.highlighted ? "border-[#D4AF37]" : "border-[#E8E4DE]/80"
                        }`}
                    >
                        <div className="flex items-start justify-between gap-2">
                            <div>
                                <p className="text-lg font-bold text-[#4A0E17]">{p.name}</p>
                                <p className="text-xs font-medium text-[#8A8178]">{p.slug}</p>
                            </div>
                            {p.highlighted && (
                                <span className="rounded-full bg-[#FDF3DC] px-2 py-0.5 text-[10px] font-bold uppercase text-[#9A7B10]">
                                    Popular
                                </span>
                            )}
                        </div>
                        <p className="mt-2 text-2xl font-extrabold text-[#4A0E17]">
                            {inr(p.price_monthly)}
                            <span className="text-sm font-medium text-[#8A8178]">/mo</span>
                        </p>
                        <p className="text-xs font-medium text-[#8A8178]">{inr(p.price_yearly)}/year · {p.trial_days}d trial</p>
                        <p className="mt-2 text-sm text-[#3D2817]">{p.description}</p>
                        <ul className="mt-3 space-y-1 text-sm text-[#3D2817]">
                            {(p.features || []).map((f) => (
                                <li key={f}>• {f}</li>
                            ))}
                        </ul>
                        <p className="mt-3 text-xs font-semibold text-[#8A8178]">
                            Up to {p.max_staff} staff · {p.max_outlets} outlet{p.max_outlets > 1 ? "s" : ""}
                            {!p.active && " · Inactive"}
                            {p.razorpay_plan_id_monthly && " · Razorpay synced"}
                        </p>
                        <div className="mt-4 flex gap-2">
                            <Button type="button" variant="outline" size="sm" onClick={() => openEdit(p)}>
                                <Pencil className="mr-1 size-3.5" /> Edit
                            </Button>
                            <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => remove(p)}>
                                <Trash2 className="mr-1 size-3.5" /> Remove
                            </Button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
